import React from "react";

import { useTranslation } from "react-i18next";
import Interweave from "interweave";
import moment from "moment";

import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Backdrop from "@material-ui/core/Backdrop";
import { Dialog, DialogContent, DialogTitle, Fade } from "@material-ui/core";

import { MassiveSendingDetailProps } from "./props";
import useStyles from "./css";
import WhatsappBackground from "../../../../assets/images/whatsapp-background.jpg"
import { ExportIcon, SideArrowForMessageIcon } from "../../../../assets/icons";
import useCustomerTerminology from "../../../../hooks/useCustomerTerminology";
import DuplicateIcon from "../../../../assets/icons/DuplicateIcon";
import DownloadFileIcon from "../../../../assets/icons/DownloadFileIcon";
import ButtonWithSpinner from "../../../common/ButtonWithSpinner";
import { ButtonPropsBorderRadius } from "../../../../models/enums/button-props-border-radius";
import { ButtonPropsBorderSide } from "../../../../models/enums/button-props-border-side";
import { ButtonPropsFill } from "../../../../models/enums/button-props-fill";
import { ButtonPropsColor } from "../../../../models/enums/button-props-color";
import { useAppSelector } from "../../../../redux/hooks";
import { selectUserInfo } from "../../../../redux/store";


export default function MassiveSendingDetail(props: MassiveSendingDetailProps) {

  const { massiveSending, open, setOpen, onClose, onReportDownload, onDuplicate } = props;
  const { t } = useTranslation("massivesendings");
  const classes = useStyles();

  const userInfo = useAppSelector(selectUserInfo);

  const goToSite = t("GO TO APPT SITE");

  const sendingTypeNames = [
    { key: 0, value: t("SingleSending") },
    { key: 1, value: t("Annual sending") },
    { key: 2, value: t("Birthdays") }
  ]

  const customerFrequencyTypes = [
    { key: 0, value: useCustomerTerminology(t("All customers"), "customers") },
    { key: 1, value: useCustomerTerminology(t("Frequent customers"), "Frequent customers") },
    { key: 2, value: useCustomerTerminology(t("Infrequent customers"), "Infrequent customers") },
    { key: 3, value: useCustomerTerminology(t("Inactive customers"), "Inactive customers") }
  ];

  const customerGenders = [
    { key: 0, value: t("AllGenders") },
    { key: 256, value: t("MaleAndFemale") },
    { key: 1, value: t("Male") },
    { key: 2, value: t("Female") },
    { key: 3, value: t("PreferNotToSay") },
    { key: 4, value: t("Custom") },
  ];

  const birthdaySendingRules = [
    { key: 0, value: t("Same day of the birthday") },
    { key: 1, value: t("One day before the birthday") },
    { key: 2, value: t("One week before the birthday") },
    { key: 3, value: t("Two weeks before the birthday") },
    { key: 4, value: t("One month before the birthday") },
  ];

  const sendingType = sendingTypeNames.find(x => x.key === massiveSending.type)!.value;
  const sendingCustomerGender = customerGenders.find(x => x.key === massiveSending.customerGender)!.value;
  const sendingCustomerFrequencyType = customerFrequencyTypes.find(x => x.key === massiveSending.customerFrequency)!.value;
  const prospectedServices = massiveSending.prospectedServices.length > 0
    ? massiveSending.prospectedServices.sort((a, b) => a.name.localeCompare(b.name)).map(s => s.name).join(", ")
    : t("None");
  const sendingBirthdaySendingRules = massiveSending.birthdaySendingType === null ? "" : birthdaySendingRules.find(x => x.key === massiveSending.birthdaySendingType)!.value;

  const sendingRules = [
    { key: 0, value: `${formattedDate(massiveSending.dateOfSending!)} - ${formattedTime(massiveSending.timeOfSending)}` },
    { key: 1, value: `${formattedDate(massiveSending.dateOfSending!)} - ${formattedTime(massiveSending.timeOfSending)}` },
    { key: 2, value: `${sendingBirthdaySendingRules} - ${formattedTime(massiveSending.timeOfSending)}` }
  ];

  const sendingRule = sendingRules.find(x => x.key === massiveSending.type)!.value;

  function formattedDate(date: Date) : string {
    return `${moment(date).format("DD")}/${t(moment(date).format("MMMM"))}/${moment(date).format("yyyy")}`;
  }
  
  function formattedTime(time: string) : string {
    const formatted = time.split(":").slice(0, 2).join(":");
    return `${formatted}hrs`;
  }
  
  interface ValueBlockProps {
    label: string;
    value: string;
  }
  
  function ValueBlock(props: ValueBlockProps) {
    return (
      <div className={classes.value}>
        <div className={classes.valueLabel}>{props.label}</div>
        <div className={classes.valueText}>{props.value}</div>
      </div>
    )
  }

  function handleCloseModal() {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  }

  function handleGoBackButtonClick() {
    handleCloseModal();
  }

  async function handleDownloadReportClick() {
    if (!massiveSending.id) return;
     await onReportDownload(massiveSending.id);
  }

  return (
    <Dialog
      aria-labelledby="massive-sending-details-title"
      aria-describedby="massive-sending-details-description"
      classes={{
        root: classes.modal,
        container: classes.container
      }}
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      PaperProps={{
        className: classes.paper,
        elevation: 0,
        square: false
      }}
      maxWidth={false}
      scroll="paper"
    >
      <DialogTitle className={classes.dialogHeader}>
        <div className={classes.modalHeader}>
          <div className={classes.modalTitle}>
            <Typography className={classes.modalTitleText} variant="h1">
              {t("Massive sending detail")}
            </Typography>
          </div>
          <div className={classes.modalActions}>
            <Button
              className={`${classes.button} ${classes.goBack}`}
              onClick={handleGoBackButtonClick}
            >
              {t('Go back')}
            </Button>          
          </div>
        </div>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Fade in={open}>
          <div className={classes.root}>
            <div className={classes.blocks}>
              <div className={classes.valuesBlock}>
                <ValueBlock label={t("Sending name")} value={massiveSending.name} />
                <ValueBlock label={useCustomerTerminology(t("Type of customers to send"), "customers")} value={`${sendingCustomerFrequencyType} / ${sendingCustomerGender}`} />
                <ValueBlock label={t("Service to prospect")} value={prospectedServices} />
                <div className={classes.divider} />
                <ValueBlock label={t("Sending type")} value={sendingType} />
                <ValueBlock label={t("Sending date")} value={sendingRule} />
                <div className={classes.divider} />
                {massiveSending.type !== 0 && (
                  <ValueBlock
                    label={t("Termination of sending")}
                    value={massiveSending.terminationDate ? formattedDate(massiveSending.terminationDate) : t("Never ends")}
                  />
                )}
              </div>
              <div className={classes.previewBlock} style={{ backgroundImage: `url(${WhatsappBackground})` }}>
                <div className={classes.messageBlock}>
                  {massiveSending.imageUrl && (
                    <div className={classes.imageBlock}>
                      <img src={massiveSending.imageUrl} alt="Preview" className={classes.image} />
                    </div>
                  )}
                  <div className={classes.text}>
                    <Interweave content={massiveSending.text} />
                  </div>
                  <SideArrowForMessageIcon className={classes.arrow} />
                  <div className={classes.timeBlock}>
                    {formattedTime(massiveSending.timeOfSending)}
                  </div>
                </div>
                <div className={classes.goToSiteButton}>
                  <div className={classes.goToSiteButtonInnerContainer}>
                    <div className={classes.goToSiteIcon}>
                      <ExportIcon color="#0BCBE0" />
                    </div>
                    {goToSite}
                  </div>
                </div>
              </div>
            </div>
            <div className={classes.buttonsContainer}>
              {userInfo.hasAccessToReports
               ? (
                <ButtonWithSpinner
                  borderRadius={ButtonPropsBorderRadius.Large}
                  borderSide={ButtonPropsBorderSide.All}
                  fill={ButtonPropsFill.Outlined}
                  backgroundOrBorderColor={ButtonPropsColor.None}
                  innerText={t('Download report of impacted customers')}
                  textColor={ButtonPropsColor.Green}
                  innerIcon={<DownloadFileIcon color="#36CE91" />}
                  onClick={handleDownloadReportClick}
                />
               ): <div />
              }
              <Button
                className={`${classes.buttonWithIcon} ${classes.duplicate}`}
                onClick={() => onDuplicate(massiveSending)}
              >
                <DuplicateIcon color="#6462F3" />
                {t('Duplicate and create new sending')}
              </Button>  
            </div>
          </div>
        </Fade>
      </DialogContent>
    </Dialog>
  );
}